import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaEye, FaTrashAlt, FaSearch, FaTimes, FaUser, 
  FaShippingFast, FaCreditCard, FaBoxOpen, FaCalendarAlt, FaTruck, FaCheckCircle, FaBan 
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, fetchVerifiedOrders } from '../api/api';

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAgent, setIsAgent] = useState<boolean>(false);
  
  const [searchIdOrName, setSearchIdOrName] = useState<string>(''); 
  const [filterStatus, setFilterStatus] = useState<string>('All'); 
  const [filterPayment, setFilterPayment] = useState<string>('All'); 
  const [filterDate, setFilterDate] = useState<string>(''); 

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  // 1. JWT Decode & Role Check
  useEffect(() => {
    const checkUserRole = () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                const payload = JSON.parse(jsonPayload);
                if (JSON.stringify(payload).toUpperCase().includes('AGENT')) {
                    setIsAgent(true);
                }
            }
        } catch (e) { console.error("Error decoding token", e); }
    };
    checkUserRole();
  }, []);

  // 2. Fetch data logic (CRM + Website)
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetching CRM Orders
      const [unverifiedRes, verifiedRes] = await Promise.all([
          fetchUnverifiedOrders().catch(() => ({ data: [] })),
          fetchVerifiedOrders().catch(() => ({ data: [] }))
      ]);

      const crmOrders = [...(unverifiedRes.data || []), ...(verifiedRes.data || [])].map((o: any) => ({
          id: o.id,
          user: {
              firstName: o.mainUser?.firstName || o.customerName || 'CRM Customer',
              lastName: o.mainUser?.lastName || '',
              email: o.mainUser?.email || o.email || 'N/A',
              mobile: o.mainUser?.mobile || o.mobile || 'N/A'
          },
          orderDate: o.createdAt || new Date().toISOString(),
          estimatedDeliveryDate: 'N/A',
          delhiveryWaybill: o.waybill || null,
          totalAmount: o.payableAmount || 0,
          status: o.status || (o.waybill ? 'SHIPPED' : 'UNVERIFIED'),
          paymentMode: 'COD',
          paymentStatus: o.status === 'DELIVERED' ? 'PAID' : 'PENDING',
          source: 'CRM',
          originalData: o
      }));

      let unifiedOrders = [];

      if (isAgent) {
         unifiedOrders = crmOrders;
      } else {
         const webRes = await axios.get('https://www.ayushvatika.com/api/orders/all', {
            headers: { 'Authorization': `Bearer ${token}` }
         }).catch(() => ({ data: [] }));

         const webOrders = (webRes.data || []).map((o: any) => ({
             ...o,
             source: 'WEBSITE',
             originalData: o
         }));
         unifiedOrders = [...webOrders, ...crmOrders];
      }

      // Sort newest first
      unifiedOrders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setOrders(unifiedOrders);

    } catch (err: any) {
      toast.error("Orders load karne mein error aayi!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [isAgent]);

  // 3. Filtering
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const customerFullName = `${order.user.firstName} ${order.user.lastName}`.toLowerCase();
      const matchesSearch = order.id.toLowerCase().includes(searchIdOrName.toLowerCase()) || 
                            customerFullName.includes(searchIdOrName.toLowerCase()) ||
                            (order.delhiveryWaybill && order.delhiveryWaybill.includes(searchIdOrName));
      
      const matchesStatus = filterStatus === 'All' || order.status === filterStatus;
      const matchesPayment = filterPayment === 'All' || 
                             order.paymentMode === filterPayment || 
                             order.paymentStatus === filterPayment;

      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;

      return matchesSearch && matchesStatus && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterStatus, filterPayment, filterDate]);

  const handleDelete = async (id: string, source: string) => {
    if (!window.confirm("Kya aap sach me ye order delete karna chahte hain?")) return;
    try {
      const token = localStorage.getItem("token");
      if (source === 'CRM') {
          await axios.delete(`https://www.ayushvatika.com/api/admin/delete-order/${id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
          });
      } else {
          await axios.delete(`https://www.ayushvatika.com/api/orders/${id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
          });
      }
      toast.success("Order deleted successfully!");
      fetchOrders();
    } catch (err: any) {
      toast.error("Delete karne mein error aayi!");
    }
  };

  const handleTrack = (waybill: string) => {
      if(!waybill) return;
      window.open(`https://www.delhivery.com/track/package/${waybill}`, '_blank');
  };

  const getStatusBadge = (status: string) => {
    const s = status?.toUpperCase() || '';
    if(s === 'DELIVERED') return <span className="px-3 py-1 rounded-lg text-xs font-black bg-green-100 text-green-700 border border-green-200 flex items-center gap-2 w-fit"><FaCheckCircle /> DELIVERED</span>;
    if(['SHIPPED', 'MANIFESTED', 'IN_TRANSIT'].includes(s)) return <span className="px-3 py-1 rounded-lg text-xs font-black bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-2 w-fit"><FaTruck /> ON THE WAY</span>;
    if(s === 'OUT_FOR_DELIVERY') return <span className="px-3 py-1 rounded-lg text-xs font-black bg-orange-100 text-orange-700 border border-orange-200 flex items-center gap-2 w-fit"><FaShippingFast /> OUT FOR DELIVERY</span>;
    if(s === 'CANCELLED') return <span className="px-3 py-1 rounded-lg text-xs font-black bg-red-100 text-red-700 border border-red-200 flex items-center gap-2 w-fit"><FaBan /> CANCELLED</span>;
    return <span className="px-3 py-1 rounded-lg text-xs font-black bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-2 w-fit"><FaBoxOpen /> {s}</span>;
  };

  return (
    <AdminLayout>
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 mb-6 transition-colors duration-200">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-8 tracking-tight">Order Management</h1>
        
        {/* --- Search Filters --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 bg-gray-50/50 dark:bg-gray-700/30 p-6 rounded-2xl border border-gray-200 dark:border-gray-600">
            <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Search Order/AWB</label>
                <div className="relative">
                    <input type="text" placeholder="ID, Name or Waybill..." className="w-full p-3 pl-10 border border-gray-300 bg-white text-gray-900 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm transition-all" value={searchIdOrName} onChange={(e) => setSearchIdOrName(e.target.value)} />
                    <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
                </div>
            </div>
             <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Status</label>
                <select className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="All">All Statuses</option>
                    <option value="PLACED">PLACED</option>
                    <option value="SHIPPED">SHIPPED</option>
                    <option value="OUT_FOR_DELIVERY">OUT FOR DELIVERY</option>
                    <option value="DELIVERED">DELIVERED</option>
                    <option value="UNVERIFIED">UNVERIFIED (CRM)</option>
                    <option value="VERIFIED">VERIFIED (CRM)</option>
                </select>
            </div>
            <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Payment</label>
                <select className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}>
                    <option value="All">All Payments</option>
                    <option value="COD">COD</option>
                    <option value="PAID">PAID</option>
                    <option value="UNPAID">UNPAID</option>
                </select>
            </div>
             <div>
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block ml-1">Date</label>
                <div className="relative">
                    <input type="date" className="w-full p-3 pl-10 border border-gray-300 bg-white text-gray-900 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
                    <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
                </div>
            </div>
        </div>

        {/* --- Data Table --- */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700 shadow-inner">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100/50 dark:bg-gray-700/50">
              <tr className="text-[11px] font-black text-gray-500 uppercase tracking-tighter">
                <th className="px-6 py-5 text-left">Order ID</th>
                <th className="px-6 py-5 text-left">Customer</th>
                <th className="px-6 py-5 text-left">Dates</th>
                <th className="px-6 py-5 text-left">Tracking (AWB)</th>
                <th className="px-6 py-5 text-left">Amount</th>
                <th className="px-6 py-5 text-left">Status</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-20 text-gray-400 italic font-medium">Loading orders...</td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-20 text-gray-400 font-bold">No orders found.</td></tr>
              ) : filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-blue-50/30 transition-all duration-200">
                  <td className="px-6 py-5 text-xs font-mono font-black text-blue-800 tracking-tight">
                      {String(order.id).substring(0, 8)} <br/>
                      <span className="text-[10px] text-gray-400 uppercase">{order.source}</span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="text-sm font-black text-gray-900">{order.user.firstName} {order.user.lastName}</div>
                    <div className="text-[10px] text-gray-400 font-medium">{order.user.mobile}</div>
                  </td>
                  <td className="px-6 py-5 text-xs text-gray-600 font-bold">
                    <div>Placed: {new Date(order.orderDate).toLocaleDateString('en-GB')}</div>
                  </td>
                  <td className="px-6 py-5">
                     {order.delhiveryWaybill ? (
                         <span className="text-xs font-mono font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded">{order.delhiveryWaybill}</span>
                     ) : <span className="text-[10px] text-gray-400 italic">Pending</span>}
                  </td>
                  <td className="px-6 py-5 text-base font-black text-gray-900">₹{order.totalAmount}</td>
                  <td className="px-6 py-5">{getStatusBadge(order.status)}</td>
                  <td className="px-6 py-5 text-center">
                    <div className="flex justify-center gap-3">
                        {order.delhiveryWaybill && (
                            <button onClick={() => handleTrack(order.delhiveryWaybill)} className="p-2.5 text-purple-600 hover:bg-purple-100 rounded-xl transition-all shadow-sm">
                                <FaTruck size={18}/>
                            </button>
                        )}
                        <button onClick={() => { setSelectedOrder(order); setShowModal(true); }} className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-xl transition-all shadow-sm">
                            <FaEye size={18}/>
                        </button>
                        {!isAgent && (
                            <button onClick={() => handleDelete(order.id, order.source)} className="p-2.5 text-red-600 hover:bg-red-100 rounded-xl transition-all shadow-sm">
                                <FaTrashAlt size={18}/>
                            </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- MODAL --- */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] max-w-4xl w-full max-h-[92vh] overflow-y-auto border-4 border-gray-50">
            <div className="sticky top-0 bg-white/80 backdrop-blur-sm px-10 py-6 border-b border-gray-100 flex justify-between items-center z-10">
              <div>
                <h2 className="text-3xl font-black text-gray-900 tracking-tighter">Order Details <span className="text-sm bg-gray-200 px-2 rounded">{selectedOrder.source}</span></h2>
                <p className="text-xs text-blue-600 font-mono mt-1 font-bold">ID: {selectedOrder.id}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-3 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-all">
                <FaTimes size={28} />
              </button>
            </div>

            <div className="p-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                  <div className="space-y-8">
                    <div>
                        <h3 className="text-xs font-black text-blue-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5"><FaUser size={16}/> Customer</h3>
                        <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 shadow-inner">
                            <p className="text-xl font-black text-gray-900">{selectedOrder.user.firstName} {selectedOrder.user.lastName}</p>
                            <p className="text-sm text-gray-500 mt-1">{selectedOrder.user.email}</p>
                            <p className="text-sm text-gray-400 mt-0.5">{selectedOrder.user.mobile}</p>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-emerald-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5"><FaShippingFast size={16}/> Shipping Point</h3>
                        <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-semibold italic">
                            {selectedOrder.source === 'WEBSITE' ? selectedOrder.originalData.shippingAddressSnapshot : `${selectedOrder.originalData.houseNumber}, ${selectedOrder.originalData.streetArea}, ${selectedOrder.originalData.city}, ${selectedOrder.originalData.state} - ${selectedOrder.originalData.pincode}`}
                        </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div>
                        <h3 className="text-xs font-black text-orange-600 uppercase tracking-[0.3em] flex items-center gap-3 mb-5"><FaCreditCard size={16}/> Status & Payment</h3>
                        <div className="bg-gray-50/50 p-7 rounded-3xl border border-gray-100 shadow-inner space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 font-bold uppercase">Status</span>
                                {getStatusBadge(selectedOrder.status)}
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 font-bold uppercase">Payment</span>
                                <span className="text-sm font-black text-gray-900">{selectedOrder.paymentMode}</span>
                            </div>
                            {selectedOrder.delhiveryWaybill && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Tracking AWB</p>
                                    <div className="flex justify-between items-center">
                                        <span className="text-lg font-mono font-bold text-purple-700">{selectedOrder.delhiveryWaybill}</span>
                                        <button onClick={() => handleTrack(selectedOrder.delhiveryWaybill)} className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded shadow-sm">Track Now</button>
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-between items-end border-t border-gray-200 pt-4 mt-4">
                                <span className="text-lg font-black text-gray-600">Total</span>
                                <span className="text-4xl font-black text-blue-700">₹{selectedOrder.totalAmount}</span>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="mt-12 pt-10 border-t-2 border-gray-50">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.4em] mb-8 flex items-center gap-3"><FaBoxOpen size={18}/> Ordered Items</h3>
                    <div className="grid grid-cols-1 gap-5">
                        {selectedOrder.source === 'WEBSITE' ? (
                            selectedOrder.originalData.orderItems?.map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-blue-300 transition-all">
                                <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0">
                                    <img src={item.imageUrl || '/Logo/default-product.png'} className="w-full h-full object-cover" alt="" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-lg font-black text-gray-900">{item.productName}</p>
                                    <p className="text-sm text-gray-400 font-bold mt-1">Qty: {item.quantity} | ₹{item.price}</p>
                                </div>
                                <div className="text-2xl font-black text-gray-900 pr-4">₹{item.quantity * item.price}</div>
                              </div>
                            ))
                        ) : (
                             <div className="flex items-center gap-6 bg-white p-5 border-2 border-gray-50 rounded-[1.5rem] hover:border-blue-300 transition-all">
                                <div className="w-20 h-20 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                                    <FaBoxOpen size={32} className="text-gray-300"/>
                                </div>
                                <div className="flex-1">
                                    <p className="text-lg font-black text-gray-900">{selectedOrder.originalData.medicine}</p>
                                    <p className="text-sm text-gray-400 font-bold mt-1">Qty: {selectedOrder.originalData.medicineQuantity} | ₹{selectedOrder.originalData.medicinePrice}</p>
                                </div>
                                <div className="text-2xl font-black text-gray-900 pr-4">₹{selectedOrder.originalData.payableAmount}</div>
                              </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="px-10 py-8 bg-gray-50/80 border-t border-gray-100 flex justify-end">
                <button onClick={() => setShowModal(false)} className="px-12 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] hover:bg-black transition-all">Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default OrderList;